import { COMPANY_STATUS_LABELS } from '@l2u/ui/lib/companyStatus.ts';
import type { ActivityEvent, CompanyStatus } from '@l2u/ui/lib/types.ts';

// ----------------------------------------------------------------------
// Activity log formatter.
//
// Turns a raw activity_log row (event_type + payload + resolved labels) into
// a human-readable {title, detail?, actor}. Two families of events exist:
//
//   1. Trigger events written by the Postgres log_activity() trigger. Their
//      event_type is `<table>.<op>` where op is the LOWERCASE tg_op
//      ('insert' | 'update' | 'delete') and payload is
//      { op: 'INSERT'|'UPDATE'|'DELETE', table, row, old_row? }. payload.row is
//      the NEW row for insert/update and the OLD row for delete. `old_row` is
//      the before-image, present ONLY on UPDATE payloads written after
//      migration 0076 — historical rows lack it and every branch below must
//      still render sensibly without it.
//
//   2. Cron events written directly by apps/api/src/scheduled.ts (e.g.
//      'company.invite_reminder.7d', 'submission_file.expired'). These carry a
//      small bespoke payload.
//
// A row may also carry `question_label`, resolved server-side
// (apps/api/src/routes/activity.ts) for submission-family events — absent on
// rows whose question has since been deleted.
//
// Every branch must tolerate missing payload keys: activity rows are an
// append-only audit trail and old rows may predate a column. Never throw.
// ----------------------------------------------------------------------

export type ActivityDescription = {
  /** One-line human summary, e.g. "Submission approved". */
  title: string;
  /** Optional secondary line with extra context, e.g. a document name. */
  detail?: string;
  /** Who performed the action, e.g. "Acme Pty Ltd" or "System". */
  actor: string;
};

/**
 * Row shape the formatter consumes — the stored event plus the resolved
 * actor_label / company_name the API attaches (already part of ActivityEvent).
 */
export type ActivityRow = ActivityEvent;

type TriggerPayload = {
  op?: string;
  table?: string;
  row?: Record<string, unknown> | null;
  // Before-image, present only on UPDATE payloads written after migration
  // 0076. Historical rows lack it — every reader tolerates its absence.
  old_row?: Record<string, unknown> | null;
};

// Internal helper return shape. `detail?: string | undefined` lets a helper
// either omit `detail` or hand back an explicit `undefined` — the project
// enables exactOptionalPropertyTypes, under which the public
// ActivityDescription's `detail?: string` would reject an explicit `undefined`.
// formatActivity normalises this back to the public shape.
type TitleDetail = { title: string; detail?: string | undefined };

// ---- small helpers ----------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Prettify a dot/underscore-separated machine code into Title-ish text. */
export function prettifyCode(code: string): string {
  const words = code.replace(/[._]/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return code;
  return words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');
}

// Review-verdict wording matching the vendor-detail review buttons
// (CompanyDetail.tsx: Compliant / Not Compliant / N/A) so the feed and the
// buttons can never disagree.
const VERDICT_LABELS: Record<string, string> = {
  approved: 'Compliant',
  rejected: 'Not Compliant',
  not_applicable: 'N/A',
};

// Wording for "(was …)" clauses — the verdicts plus the unreviewed state.
const WAS_LABELS: Record<string, string> = {
  ...VERDICT_LABELS,
  pending: 'Pending review',
};

/**
 * Who performed the action. Admin actors show their resolved name/email;
 * company actors show the company name; system/cron actors show "System".
 */
export function describeActor(row: ActivityRow): string {
  if (row.actor_type === 'system') return 'System';
  if (row.actor_type === 'admin') {
    return asString(row.actor_label) ?? 'An administrator';
  }
  // company actor
  return asString(row.company_name) ?? asString(row.actor_label) ?? 'The vendor';
}

// ---- trigger-event humanisers ----------------------------------------

function describeSubmission(
  op: string,
  row: Record<string, unknown>,
  oldRow: Record<string, unknown> | null,
): TitleDetail {
  if (op === 'DELETE') return { title: 'Submission removed' };
  if (op === 'INSERT') return { title: 'Submission created' };

  // UPDATE — the most informative case.
  const status = asString(row.status);
  const oldStatus = oldRow ? asString(oldRow.status) : null;

  // With a before-image and an UNCHANGED status this is not a verdict event:
  // either a draft-only review (verdict staged, not yet published) or plain
  // bookkeeping. Detectable only with old_row; historical rows fall through.
  if (oldRow && status === oldStatus) {
    // An admin closing an approved document's conversation stamps only
    // conversation_closed_at (the status never moves).
    const closedAt = asString(row.conversation_closed_at);
    if (closedAt && closedAt !== asString(oldRow.conversation_closed_at)) {
      return { title: 'Conversation closed' };
    }
    const draft = asString(row.draft_status);
    const oldDraft = oldRow ? asString(oldRow.draft_status) : null;
    if (draft && draft !== oldDraft && WAS_LABELS[draft]) {
      const label = draft === 'pending' ? 'reset' : WAS_LABELS[draft];
      return { title: `Review drafted — ${label}` };
    }
    return { title: 'Submission updated' };
  }

  if (status && VERDICT_LABELS[status]) {
    const was =
      oldStatus && oldStatus !== status && WAS_LABELS[oldStatus]
        ? ` (was ${WAS_LABELS[oldStatus]})`
        : '';
    const comment = asString(row.reviewer_comment);
    return {
      title: `Question marked ${VERDICT_LABELS[status]}${was}`,
      detail: status === 'rejected' && comment ? `Reviewer comment: ${comment}` : undefined,
    };
  }
  if (status === 'pending') {
    return { title: 'Submission status changed to Pending review' };
  }
  return { title: 'Submission updated' };
}

function describeSubmissionFile(
  op: string,
  row: Record<string, unknown>,
  oldRow: Record<string, unknown> | null,
): TitleDetail {
  const name = asString(row.file_name);
  const detail = name ? `File: ${name}` : undefined;
  if (op === 'INSERT') return { title: 'Document uploaded', detail };
  if (op === 'DELETE') return { title: 'Document removed', detail };
  // UPDATE — vendor "remove" is a soft-delete that stamps superseded_at (kept
  // for audit history), so it surfaces here as an UPDATE rather than a DELETE.
  // Check that first: a superseded row is a removal regardless of any expiry
  // it also carries.
  if (asString(row.superseded_at)) {
    return { title: 'Document removed', detail };
  }
  // Otherwise an UPDATE is most often an expiry date being set/changed; with a
  // before-image (0076+) show what it changed from.
  const expiresAt = asString(row.expires_at);
  if (expiresAt) {
    const oldExpiry = oldRow ? asString(oldRow.expires_at) : null;
    const was = oldExpiry && oldExpiry !== expiresAt ? ` (was ${oldExpiry})` : '';
    return {
      title: 'Document expiry updated',
      detail: name ? `${name} — expires ${expiresAt}${was}` : `Expires ${expiresAt}${was}`,
    };
  }
  return { title: 'Document updated', detail };
}

function describeSubmissionAdoption(op: string, row: Record<string, unknown>): TitleDetail {
  const mode = asString(row.mode);
  if (op === 'DELETE') return { title: 'Policy document choice removed' };
  if (mode === 'adopted') {
    return {
      title: 'Standard policy adopted',
      detail: 'Vendor adopted the supplied policy document',
    };
  }
  if (mode === 'uploaded') {
    return { title: 'Own policy uploaded', detail: 'Vendor uploaded their own policy document' };
  }
  return {
    title: op === 'INSERT' ? 'Policy document choice recorded' : 'Policy document choice updated',
  };
}

function describeCompany(
  op: string,
  row: Record<string, unknown>,
  oldRow: Record<string, unknown> | null,
): TitleDetail {
  if (op === 'INSERT') {
    const name = asString(row.company_name);
    return { title: 'Vendor invited', detail: name ?? undefined };
  }
  if (op === 'DELETE') {
    const name = asString(row.company_name);
    return { title: 'Vendor deleted', detail: name ?? undefined };
  }
  // UPDATE — surface a status change using the shared status labels so the
  // wording (incl. "Not Compliant" for the legacy 'expired' status) never
  // drifts from the rest of the admin UI. With a before-image (0076+), claim a
  // status change only when one actually happened: the full-row payload always
  // carries `status`, so without this every company edit read as "Vendor
  // status set to …".
  const status = asString(row.status);
  const oldStatus = oldRow ? asString(oldRow.status) : null;
  // An invite resend writes no event of its own: POST /api/companies/:id/invite
  // (the only writer of invited_at) re-stamps invited_at, and this trigger row
  // is the record of it. A first invite has no prior invited_at and is read as
  // its status change below. A resend moves expires_at only when the deadline
  // had lapsed and was re-anchored.
  const invitedAt = asString(row.invited_at);
  const oldInvitedAt = oldRow ? asString(oldRow.invited_at) : null;
  if (oldInvitedAt && invitedAt && invitedAt !== oldInvitedAt) {
    const renewed = oldRow && asString(row.expires_at) !== asString(oldRow.expires_at);
    return {
      title: 'Invite resent',
      detail: renewed ? 'Completion deadline renewed' : undefined,
    };
  }
  if (oldRow && status === oldStatus) {
    return { title: 'Vendor record updated' };
  }
  if (status && status in COMPANY_STATUS_LABELS) {
    const label = COMPANY_STATUS_LABELS[status as CompanyStatus];
    const was =
      oldStatus && oldStatus in COMPANY_STATUS_LABELS
        ? ` (was ${COMPANY_STATUS_LABELS[oldStatus as CompanyStatus]})`
        : '';
    return { title: `Vendor status set to ${label}${was}` };
  }
  return { title: 'Vendor record updated' };
}

const TABLE_NOUNS: Record<string, string> = {
  documents: 'Document template',
  questions: 'Question',
  question_categories: 'Question category',
  questionnaires: 'Questionnaire',
  settings: 'Settings',
  review_notifications: 'Review note',
};

function describeGenericTable(table: string, op: string): TitleDetail {
  const noun = TABLE_NOUNS[table] ?? prettifyCode(table);
  const verb = op === 'INSERT' ? 'created' : op === 'DELETE' ? 'deleted' : 'updated';
  // 'Settings updated' reads better than 'Settings created/deleted'; settings
  // is a singleton row that is only ever updated in practice.
  if (table === 'settings') return { title: 'Settings updated' };
  return { title: `${noun} ${verb}` };
}

function describeTriggerEvent(eventType: string, payload: TriggerPayload): TitleDetail {
  const table = asString(payload.table) ?? eventType.split('.')[0] ?? eventType;
  const op = (asString(payload.op) ?? eventType.split('.')[1] ?? '').toUpperCase();
  const row = asRecord(payload.row) ?? {};
  const oldRow = asRecord(payload.old_row);

  switch (table) {
    case 'submissions':
      return describeSubmission(op, row, oldRow);
    case 'submission_files':
      return describeSubmissionFile(op, row, oldRow);
    case 'submission_adoptions':
      return describeSubmissionAdoption(op, row);
    case 'companies':
      return describeCompany(op, row, oldRow);
    default:
      return describeGenericTable(table, op);
  }
}

// ---- cron-event humanisers --------------------------------------------

function describeCronEvent(
  eventType: string,
  payload: Record<string, unknown>,
): TitleDetail | null {
  // company.invite_reminder.<n>d
  const inviteMatch = /^company\.invite_reminder\.(\d+)d$/.exec(eventType);
  if (inviteMatch) {
    const days = inviteMatch[1];
    return { title: `Invite reminder sent (${days} days)` };
  }

  // company.compliance_expiring.<n>d
  const complianceMatch = /^company\.compliance_expiring\.(\d+)d$/.exec(eventType);
  if (complianceMatch) {
    const days = complianceMatch[1];
    return { title: `Compliance-expiry reminder sent (${days} days)` };
  }

  if (eventType === 'company.mfa_reset') {
    const removed = typeof payload.removed === 'number' ? payload.removed : null;
    const total = typeof payload.total === 'number' ? payload.total : null;
    return {
      title: 'Two-factor authentication reset',
      detail:
        removed === null
          ? undefined
          : `${removed} factor${removed === 1 ? '' : 's'} removed${
              total !== null && total !== removed ? ` of ${total}` : ''
            }`,
    };
  }

  if (eventType === 'company.login_locked') {
    // No duration: the payload is only { ip }, and the lock is 15 minutes or,
    // under the 6-hour cap (E7), until that window ends.
    const ip = asString(payload.ip);
    return {
      title: 'Sign-in locked after too many incorrect codes',
      detail: ip ? `From ${ip}` : undefined,
    };
  }

  if (eventType === 'company.login_link_sent') {
    return { title: 'Login link sent' };
  }

  if (eventType === 'submission_file.expiry_reminder') {
    const name = asString(payload.file_name);
    const days = typeof payload.days_remaining === 'number' ? payload.days_remaining : null;
    return {
      title: 'Document expiry reminder sent',
      detail:
        name && days !== null
          ? `${name} — expires in ${days} day${days === 1 ? '' : 's'}`
          : (name ?? undefined),
    };
  }

  if (eventType === 'submission_file.expired') {
    const name = asString(payload.file_name);
    return {
      title: 'Document marked expired — vendor notified',
      detail: name ?? undefined,
    };
  }

  return null;
}

// ---- relative time ----------------------------------------------------

/**
 * Compact relative time, e.g. "just now", "5m ago", "3h ago", "2d ago".
 * Beyond a week it returns the local date so the audit trail stays precise.
 * `now` is injectable for deterministic tests.
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = now - then;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 45) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(then).toLocaleDateString();
}

// ---- public entry point -----------------------------------------------

/**
 * Format a single activity row into a human-readable description. Pure and
 * total: any unrecognised event or missing payload yields a graceful
 * prettified fallback rather than throwing.
 */
export function formatActivity(row: ActivityRow): ActivityDescription {
  const actor = describeActor(row);
  const eventType = typeof row.event_type === 'string' ? row.event_type : '';
  const payload = asRecord(row.payload) ?? {};

  // A trigger event carries an explicit op + table; that is the reliable
  // discriminator (cron events never set those keys).
  let base: TitleDetail;
  if (asString(payload.op) && asString(payload.table)) {
    base = describeTriggerEvent(eventType, payload as TriggerPayload);
  } else {
    // Cron/bespoke events, else the prettified raw code so the UI never shows
    // a blank or a crash for an event type added after this formatter.
    base = describeCronEvent(eventType, payload) ?? {
      title: eventType ? prettifyCode(eventType) : 'Activity',
    };
  }

  // Server-resolved question name (routes/activity.ts): one uniform
  // "Question: …" prefix names what the event touched. Absent on historical
  // rows enriched before the field existed and on deleted questions — the
  // detail simply stays as it was.
  const questionLabel = asString(row.question_label);
  const detail = questionLabel
    ? base.detail
      ? `Question: ${questionLabel} — ${base.detail}`
      : `Question: ${questionLabel}`
    : base.detail;

  return detail ? { title: base.title, detail, actor } : { title: base.title, actor };
}
